/**
 * Cart API Service
 *
 * Handles all cart-related API calls to the backend CustomerCartController.
 *
 * HARD-011: Session ID is now carried by the HttpOnly stork_sid cookie set
 * server-side.  The axios instance has `withCredentials: true` so the cookie
 * is sent automatically.  The legacy X-Session-ID header and getSessionId()
 * import have been removed.
 */

import api from '@/lib/axios';
import {
    CustomerCartResponse,
    CustomerAddToCartRequest,
    CartPricingBreakdown,
    ItemPricingBreakdown,
    PricingCalculationRequest,
    CartItemPricingRequest,
} from '@/types/cart';

// =============================================================================
// Cart Operations
// =============================================================================

/**
 * Get the current cart contents
 */
export const getCart = async (signal?: AbortSignal): Promise<CustomerCartResponse> => {
    const response = await api.get<CustomerCartResponse>('/customer/cart', { signal });
    return response.data;
};

/**
 * Add an item to the cart
 */
export const addToCart = async (
    request: CustomerAddToCartRequest
): Promise<CustomerCartResponse> => {
    const response = await api.post<CustomerCartResponse>(
        '/customer/cart/items',
        request
    );
    return response.data;
};

/**
 * Update cart item quantity
 */
export const updateCartItem = async (
    itemId: string,
    quantity: number
): Promise<CustomerCartResponse> => {
    const response = await api.put<CustomerCartResponse>(
        `/customer/cart/items/${itemId}`,
        { quantity }
    );
    return response.data;
};

/**
 * Remove an item from the cart
 */
export const removeCartItem = async (
    itemId: string
): Promise<CustomerCartResponse> => {
    const response = await api.delete<CustomerCartResponse>(
        `/customer/cart/items/${itemId}`
    );
    return response.data;
};

/**
 * Clear the entire cart
 */
export const clearCart = async (): Promise<void> => {
    await api.delete('/customer/cart');
};

// =============================================================================
// Pricing Operations
// =============================================================================

/**
 * Calculate pricing for the entire cart
 */
export const calculateCartPricing = async (
    request: PricingCalculationRequest
): Promise<CartPricingBreakdown> => {
    const response = await api.post<CartPricingBreakdown>(
        '/customer/cart/pricing',
        request
    );
    return response.data;
};

/**
 * Calculate pricing for a single item (for product detail page)
 */
export const calculateItemPricing = async (
    request: CartItemPricingRequest
): Promise<ItemPricingBreakdown> => {
    const response = await api.post<ItemPricingBreakdown>(
        '/customer/cart/items/pricing',
        request
    );
    return response.data;
};

// =============================================================================
// Validation Operations (for checkout)
// =============================================================================

/**
 * Request to validate cart items for availability
 */
export interface CartValidationRequest {
    startDate: string;
    endDate: string;
    locationId: string;
}

/**
 * Response containing detailed validation results for all cart items
 */
export interface CartValidationResult {
    isValid: boolean;
    items: CartItemValidation[];
    accessoryItems: CartAccessoryValidation[];
    updatedTotals: CartValidationTotals;
    errors: string[];
}

/**
 * Validation result for a single cart item (equipment)
 */
export interface CartItemValidation {
    itemId: string;
    equipmentTypeId: string;
    equipmentName: string;
    isAvailable: boolean;
    availableQuantity: number;
    requestedQuantity: number;
    canFulfill: boolean;
    priceChanged: boolean;
    newUnitPrice?: number;
    originalUnitPrice?: number;
    error?: string;
}

/**
 * Validation result for a single cart accessory item
 */
export interface CartAccessoryValidation {
    itemId: string;
    accessoryId: string;
    accessoryName: string;
    isAvailable: boolean;
    availableQuantity: number;
    requestedQuantity: number;
    canFulfill: boolean;
    priceChanged: boolean;
    newUnitPrice?: number;
    originalUnitPrice?: number;
    forCartItemId?: string;
    error?: string;
}

/**
 * Updated cart totals after validation
 */
export interface CartValidationTotals {
    subtotal: number;
    taxAmount: number;
    discountAmount: number;
    total: number;
    totalDays: number;
}

/**
 * Validate cart items for availability
 * This checks if all items in the cart are still available for the selected dates
 */
export const validateCart = async (
    request?: CartValidationRequest
): Promise<CartValidationResult> => {
    const response = await api.post<CartValidationResult>(
        '/customer/cart/validate',
        request || {}
    );
    return response.data;
};

// =============================================================================
// Cart Transfer (for when guest logs in)
// =============================================================================

/**
 * Transfer guest cart to authenticated user's account.
 * Called after a user logs in or registers.
 * Deactivates any previous user carts — the session cart becomes the active cart.
 */
export const mergeCart = async (sessionId: string): Promise<CustomerCartResponse> => {
    // Note: The backend endpoint is /transfer but we keep the function name for compatibility
    const response = await api.post<CustomerCartResponse>(
        '/customer/cart/transfer',
        { sessionId },
    );
    return response.data;
};

export default {
    getCart,
    addToCart,
    updateCartItem,
    removeCartItem,
    clearCart,
    calculateCartPricing,
    calculateItemPricing,
    validateCart,
    mergeCart,
};
